import React, {FC, useMemo} from 'react';
import {css, FormControl, IconButton, MenuItem, Select, useTheme} from '@mui/material';
import {GetStylesFunction, utils} from '@wallaby-ui/core';
import moment from 'moment';
import {SelectChangeEvent} from '@mui/material/Select/SelectInput';
import {PickersCalendarHeaderProps} from '@mui/x-date-pickers/PickersCalendarHeader/PickersCalendarHeader.types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useIntl} from 'react-intl';

interface OwnProps extends PickersCalendarHeaderProps<Date> {
  enableFuture?: boolean;
  boundMinDate?: Date | null;
  boundMaxDate?: Date | null;
}

type Props = OwnProps;

const DEFAULT_MIN_DATE = new Date(1900, 0, 1);
const DEFAULT_MAX_DATE = new Date(2099, 11, 31);

export const FilterNodeTypeDateRangeCalendarHeader: FC<Props> = (props: Props) => {
  const {enableFuture, disablePast, currentMonth, onMonthChange, views, minDate, maxDate, boundMinDate, boundMaxDate} = props;

  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const intl = useIntl();

  const months = moment.months();

  const today = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  const baseMinDate = boundMinDate ?? minDate ?? DEFAULT_MIN_DATE;
  const baseMaxDate = boundMaxDate ?? maxDate ?? (enableFuture ? DEFAULT_MAX_DATE : today);
  const minFilterDate = disablePast && today > baseMinDate ? today : baseMinDate;
  const maxFilterDate = baseMaxDate;

  const getYearsRange = (start: number, stop: number, step: number) =>
    Array.from({length: Math.floor((stop - start) / step) + 1}, (_, i) => start + i * step);
  const years = getYearsRange(minFilterDate.getFullYear(), maxFilterDate.getFullYear(), 1);

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const monthIndex = months.indexOf(event.target.value);
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(monthIndex);
    onMonthChange(newMonth, 'left');
    event.stopPropagation();
    event.preventDefault();
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const year = Number(event.target.value);
    const newYear = new Date(currentMonth);
    newYear.setFullYear(year);
    onMonthChange(newYear, 'left');
    event.stopPropagation();
    event.preventDefault();
  };

  const handleSelectPreviousMonth = () => {
    const previousMonth = new Date(currentMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    onMonthChange(previousMonth, 'right');
  };

  const handleSelectNextMonth = () => {
    const futureMonth = new Date(currentMonth);
    futureMonth.setMonth(futureMonth.getMonth() + 1);
    onMonthChange(futureMonth, 'left');
  };

  return (
    <div css={muiStyles.calendarHeaderWrapper}>
      <IconButton
        onClick={handleSelectPreviousMonth}
        disabled={currentMonth.getFullYear() === minFilterDate.getFullYear() && currentMonth.getMonth() === minFilterDate.getMonth()}
      >
        <ChevronLeftIcon />
      </IconButton>
      <div css={muiStyles.selectWrapper}>
        {views.includes('month') && (
          <FormControl size='small'>
            <Select
              css={muiStyles.select}
              sx={{width: '130px'}}
              MenuProps={{
                PaperProps: {sx: {maxHeight: 300}},
              }}
              value={months[currentMonth?.getMonth()]}
              label='Month'
              onChange={handleMonthChange}
            >
              {months.map((month, index) => (
                <MenuItem key={index} value={month}>
                  {intl && utils.TranslateUtils.translate(`date.months.${month.toLowerCase()}`, intl)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {views.includes('year') && (
          <FormControl size='small'>
            <Select
              css={muiStyles.select}
              value={currentMonth.getFullYear()}
              sx={{width: '100px'}}
              renderValue={(selected) => selected}
              label='Year'
              onChange={handleYearChange}
              MenuProps={{
                PaperProps: {sx: {maxHeight: 300}},
              }}
            >
              {years.map((year, index) => (
                <MenuItem key={index} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <IconButton
        onClick={handleSelectNextMonth}
        disabled={currentMonth.getFullYear() === maxFilterDate.getFullYear() && currentMonth.getMonth() === maxFilterDate.getMonth()}
      >
        <ChevronRightIcon />
      </IconButton>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  calendarHeaderWrapper: css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding-left: 2px;
    padding-right: 2px;
  `,
  selectWrapper: css`
    display: flex;
    gap: ${options?.theme?.spacing(2)};
  `,
  select: css`
    padding-top: ${options?.theme?.spacing(0.5)} !important;

    svg {
      right: 18px !important;
    }
  `,
});
